import { useState, useCallback } from 'react';
import { validateProjectName } from '../utils';
import { RepoData } from '../types';

interface FormState {
  projectName: string;
  projectNameError: string;
  projectType: string;
  selectedOrganization: string;
  selectedNamespace: string;
  selectedHost: string;
  description: string;
}

interface FormActions {
  handleProjectNameChange: (value: string) => void;
  setProjectType: (value: string) => void;
  setSelectedOrganization: (value: string) => void;
  setSelectedNamespace: (value: string) => void;
  setSelectedHost: (value: string) => void;
  setDescription: (value: string) => void;
}

/**
 * Hook to manage form state
 */
export const useFormState = (
  defaultProjectType: string,
  defaultOrganization: string,
  defaultNamespace: string,
  defaultHost: string,
  initialFormData?: Partial<RepoData>,
): [FormState, FormActions] => {
  const [projectName, setProjectName] = useState(
    initialFormData?.projectName || '',
  );
  const [projectNameError, setProjectNameError] = useState('');
  const [projectType, setProjectType] = useState(
    initialFormData?.projectType || defaultProjectType,
  );
  const [selectedOrganization, setSelectedOrganization] = useState(
    initialFormData?.organization || defaultOrganization,
  );
  const [selectedNamespace, setSelectedNamespace] = useState(
    initialFormData?.namespace || defaultNamespace,
  );
  const [selectedHost, setSelectedHost] = useState(
    initialFormData?.host || defaultHost,
  );
  const [description, setDescription] = useState(
    initialFormData?.description || '',
  );

  const handleProjectNameChange = useCallback((value: string) => {
    setProjectName(value);
    const error = validateProjectName(value);
    setProjectNameError(error);
  }, []);

  const formState: FormState = {
    projectName,
    projectNameError,
    projectType,
    selectedOrganization,
    selectedNamespace,
    selectedHost,
    description,
  };

  const formActions: FormActions = {
    handleProjectNameChange,
    setProjectType,
    setSelectedOrganization,
    setSelectedNamespace,
    setSelectedHost,
    setDescription,
  };

  return [formState, formActions];
};
